// ─────────────────────────────────────────────────────────────────────────────
// src/db/syncEngine.js
// Motor de sincronización: sube las ventas pendientes al servidor cuando
// regresa el internet. Usa el endpoint batch /sales/sync-offline.
// ─────────────────────────────────────────────────────────────────────────────

import db, { getPendingSales, countPendingSales } from './localDB';
import { salesAPI } from '../services/api';

/**
 * Sincroniza todas las ventas pendientes del usuario con el servidor.
 * Usa el endpoint batch POST /sales/sync-offline.
 *
 * Estrategia de stock (Regla de Oro del Retail):
 * - El backend SIEMPRE procesa la venta (nunca la rechaza).
 * - Si hay stock negativo resultante, devuelve warnings[].
 * - El frontend muestra alerta suave al dueño.
 *
 * @param {number} userId
 * @param {function} onProgress - Callback opcional: ({ synced, total, warnings })
 * @returns {{ synced: number, errors: number, warnings: Array }}
 */
export async function syncPendingSales(userId, onProgress) {
  const pending = await getPendingSales(userId);

  if (pending.length === 0) {
    return { synced: 0, errors: 0, warnings: [] };
  }

  // Preparar el batch con offline_id para poder correlacionar la respuesta
  const salesBatch = pending.map(sale => ({
    ...sale.data,
    offline_id: sale.id   // ID de Dexie para saber cuál actualizar
  }));

  // Marcar todas como "syncing" antes del request
  for (const sale of pending) {
    await db.pending_sales.update(sale.id, { sync_status: 'syncing' });
  }

  try {
    // Enviar todo el batch en una sola llamada al backend
    const response  = await salesAPI.syncOffline(salesBatch);
    const { results = [], warnings = [] } = response.data;

    // Actualizar el estado de cada venta según la respuesta del servidor
    for (const result of results) {
      const dexieId = result.offline_id;
      if (!dexieId) continue;

      if (result.success) {
        await db.pending_sales.update(dexieId, {
          sync_status:    'synced',
          synced_at:      new Date().toISOString(),
          server_sale_id: result.sale_id
        });
      } else {
        const sale        = pending.find(s => s.id === dexieId);
        const retryCount  = (sale?.retry_count || 0) + 1;
        await db.pending_sales.update(dexieId, {
          sync_status: retryCount >= 3 ? 'error' : 'pending',
          retry_count: retryCount,
          last_error:  result.error || 'Error del servidor'
        });
      }
    }

    const synced = results.filter(r => r.success).length;
    const errors = results.filter(r => !r.success).length;

    await db.sync_log.add({
      type:          'batch_sync',
      status:        'success',
      timestamp:     new Date().toISOString(),
      error_message: null
    });

    onProgress?.({ synced, total: pending.length, warnings });
    return { synced, errors, warnings };

  } catch (error) {
    // Error de red — revertir todas a "pending" para reintentar
    for (const sale of pending) {
      await db.pending_sales.update(sale.id, {
        sync_status: 'pending',
        last_error:  error.message
      });
    }

    await db.sync_log.add({
      type:          'batch_sync',
      status:        'error',
      timestamp:     new Date().toISOString(),
      error_message: error.message
    });

    console.error('[SyncEngine] Error en batch sync:', error.message);
    return { synced: 0, errors: pending.length, warnings: [] };
  }
}


/**
 * Registra el listener del evento 'online' como fallback.
 * Se usa cuando Background Sync API no está disponible (Safari, Firefox).
 *
 * @param {number} userId
 * @param {function} onSyncComplete - Callback con resultado de sincronización
 */
export function registerOnlineFallback(userId, onSyncComplete) {
  const handleOnline = async () => {
    console.log('[Sync] Conexión restaurada — iniciando sincronización...');
    const result = await syncPendingSales(userId);
    if (result.synced > 0 || result.warnings.length > 0) {
      onSyncComplete?.(result);
    }
  };

  window.addEventListener('online', handleOnline);

  // Retorna función de limpieza para usar en useEffect
  return () => window.removeEventListener('online', handleOnline);
}

/**
 * Registra Background Sync en el Service Worker (Chrome/Edge).
 * Si no está disponible, usa el fallback de evento 'online'.
 *
 * @param {number} userId
 * @param {function} onSyncComplete
 */
export async function registerBackgroundSync(userId, onSyncComplete) {
  // Siempre registramos el fallback (funciona en todos los navegadores)
  const cleanup = registerOnlineFallback(userId, onSyncComplete);

  // Adicionalmente, intentamos Background Sync API (Chrome/Edge)
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.sync.register('sync-pending-sales');
      console.log('[Sync] Background Sync API registrado (Chrome/Edge).');
    } catch (err) {
      console.log('[Sync] Background Sync no disponible — usando fallback online event.');
    }
  }

  return cleanup;
}

/**
 * Obtiene el conteo de ventas pendientes de un usuario.
 * Para mostrar el badge en la UI.
 */
export async function getPendingCount(userId) {
  return await countPendingSales(userId);
}
